import { Component, computed, DestroyRef, inject, OnInit, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { FrontendConfigurationService } from '@tourfold/shared-kernels/frontend-configuration';
import {
  FrontendVersionBannerComponent,
  ThemeInjectorService,
  TenantThemePayload,
} from '@tourfold/libraries/shared-ui';
import { HeaderActionsService } from '@tourfold/libraries/shared-ui/header-actions/header-actions.service';
import { CurrentUserInfoService } from '@tourfold/libraries/shared-ui/header-actions/current-user-info.service';
import { userInitials } from '@tourfold/libraries/shared-ui/header-actions/user-initials';
import { RouterOutlet } from '@angular/router';
import { FrontendVersionService, reloadOnceForMinimum } from '@tourfold/libraries/shared-util';
import { PrimeNG } from 'primeng/config';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './services/auth.service';
import { applyFavicon } from './favicon.util';
import { CurrentUserService } from '@tourfold/shared-kernels/current-user';
import { FrontendUserPreferencesApi } from '@tourfold/libraries/tf-api-client-frontend-v2';
import { UserPreferencesService, ThemeMode } from '@tourfold/shared-kernels/user-preferences';
import {
  LANGUAGE_OPTIONS,
  DocumentLocaleAttributesService,
  languageTagToTranslationLang,
  PlatformI18nService,
} from '@tourfold/shared-kernels/i18n';

@Component({
  selector: 'tf-web-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, FrontendVersionBannerComponent],
})
export class AppComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly currentUserService = inject(CurrentUserService);
  protected readonly versionService = inject(FrontendVersionService);
  private readonly userPreferencesService = inject(UserPreferencesService);
  private readonly userPreferencesApi = inject(FrontendUserPreferencesApi);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly titleService = inject(Title);
  private readonly tenantService = inject(FrontendConfigurationService);
  private readonly themeInjector = inject(ThemeInjectorService);
  private readonly translateService = inject(TranslateService);
  private readonly platformI18n = inject(PlatformI18nService);
  private readonly documentAttributes = inject(DocumentLocaleAttributesService);
  private readonly primeNGConfig = inject(PrimeNG);
  private readonly headerActionsService = inject(HeaderActionsService);
  private readonly currentUserInfoService = inject(CurrentUserInfoService);

  private readonly isDarkMode = computed(() => {
    return this.tenantService.resolvedThemeMode() === 'dark';
  });

  /**
   * Whether the user has a usable session. Gated on token presence (via
   * `AuthService.isAuthenticated`), not on the `/me` profile signal — the
   * profile loads asynchronously and is briefly null right after a reload.
   */
  private readonly isAuthenticated = this.authService.isAuthenticated;

  constructor() {
    this.documentAttributes.startSync();
    // Restore session from localStorage if tokens exist
    this.authService.restoreSession();
    // Load the authenticated config (tenant + resource-access/permissions +
    // user preferences) and the current-user profile from the API, so guarded
    // routes, action buttons, theme/language, and the profile popover are all
    // populated again after a hard reload.
    this.authService.hydrateAuthenticatedSession();
    effect(() => {
      const tenantName = this.tenantService.tenantName() ?? '';
      this.titleService.setTitle(tenantName ? `Tourfold | ${tenantName}` : 'Tourfold');

      applyFavicon(this.document, this.tenantService.favicon());
    });

    // Design tokens are filled from the API theme (theme.light / theme.dark from frontend-configuration).
    // applyTheme() builds the PrimeNG preset and :root CSS vars from that object.
    effect(() => {
      const theme = this.tenantService.effectiveTheme();
      const mode = this.tenantService.resolvedThemeMode();
      if (!theme?.light) {
        this.themeInjector.injectTenantCSSVariables(ThemeInjectorService.getDefaultCssVars(mode));
        return;
      }
      const payload: TenantThemePayload = {
        light: theme.light,
        dark: theme.dark,
      };
      this.themeInjector.applyTheme(payload, mode);
    });

    effect(() => {
      this.applyThemeMode(this.tenantService.resolvedThemeMode());
    });

    // Wire shared header-actions (theme toggle / sign out) at root level
    // so they keep working across app-shell and settings-shell routes.
    effect(() => {
      this.headerActionsService.setDarkMode(this.isDarkMode());
      this.headerActionsService.setThemeMode(this.tenantService.effectiveThemeMode());
      this.userPreferencesService.setThemeHint(this.tenantService.resolvedThemeMode());
    });
    effect(() => {
      this.headerActionsService.setLanguage(this.platformI18n.resolveLanguageTag());
    });
    effect(() => {
      const language = this.userPreferencesService.language();
      if (!language) {
        return;
      }
      this.documentAttributes.applyLanguageTag(language);
      const translateLang = languageTagToTranslationLang(language);
      if (this.translateService.getCurrentLang() !== translateLang) {
        from(this.translateService.use(translateLang)).subscribe();
      }
    });
    this.headerActionsService.themeToggle
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.toggleThemeMode());
    this.headerActionsService.themeModeChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((mode) => this.setThemeMode(mode));
    this.headerActionsService.languageChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((language) => this.setLanguage(language));
    this.headerActionsService.signOut
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.authService.logout());
    this.headerActionsService.setLanguageOptions(
      LANGUAGE_OPTIONS.map(({ id, label }) => ({ id, label }))
    );

    // Keep the shared CurrentUserInfoService in sync with the current user at
    // the root level so the profile popover renders correctly across every
    // shell (app-shell, settings-shell, etc.) and after a hard reload.
    effect(() => {
      const u = this.currentUserService.user();
      if (!u) {
        this.currentUserInfoService.setInfo(null);
        return;
      }
      const firstname = u.firstname ?? '';
      const lastname = u.lastname ?? '';
      const name = `${firstname} ${lastname}`.trim();
      const initial = userInitials(firstname, lastname);
      this.currentUserInfoService.setInfo({
        initial,
        name,
        email: u.email ?? '',
      });
    });

    // Frontend version mismatch (TF-1459): hard reload when public, show banner
    // when authenticated. `reloadOnceForMinimum` guards against an infinite
    // reload loop if the deployed bundle is below the backend's declared
    // minimum.
    effect(() => {
      if (this.versionService.isTooLow() && !this.isAuthenticated()) {
        reloadOnceForMinimum(this.versionService.minimumRequired());
      }
    });

    // Note: SSE connection is initiated by the funeral business domain component
    // when it loads, ensuring auth is ready and connection is scoped appropriately.
  }

  protected onVersionBannerReload(): void {
    // Explicit user gesture — bypass the once-per-minimum sessionStorage guard
    // (the guard is only there to break unattended auto-reload loops on the
    // public path). The user pressing the button expects a reload to happen now.
    globalThis.location.reload();
  }
  ngOnInit(): void {
    this.applyPrimeNgTranslations();
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyPrimeNgTranslations());
  }

  private applyPrimeNgTranslations(): void {
    this.primeNGConfig.setTranslation({
      dayNames: this.translateService.instant('SHARED.DATE.WEEKDAYS'),
      dayNamesMin: this.translateService.instant('SHARED.DATE.WEEKDAYS_SHORT'),
      dayNamesShort: this.translateService.instant('SHARED.DATE.WEEKDAYS_SHORT'),
      monthNames: this.translateService.instant('SHARED.DATE.MONTHS'),
      monthNamesShort: this.translateService.instant('SHARED.DATE.MONTHS_SHORT'),
    });
  }

  private toggleThemeMode(): void {
    const newMode: ThemeMode = this.isDarkMode() ? 'light' : 'dark';
    this.setThemeMode(newMode);
  }

  private setThemeMode(newMode: ThemeMode): void {
    if (this.tenantService.effectiveThemeMode() === newMode) {
      return;
    }
    this.tenantService.setThemeModeOverride(newMode);
    this.userPreferencesService.setThemeMode(newMode);
    from(
      this.userPreferencesApi.patchCurrentUserPreferences({
        patchFrontendUserPreferences: { appearance: { themeMode: newMode } },
      })
    ).subscribe();
  }

  private setLanguage(language: string): void {
    if (this.userPreferencesService.language() === language) {
      return;
    }
    this.userPreferencesService.setLanguage(language);
    this.documentAttributes.applyLanguageTag(language);
    from(
      this.userPreferencesApi.patchCurrentUserPreferences({
        patchFrontendUserPreferences: { localization: { language } },
      })
    ).subscribe();
  }

  private applyThemeMode(themeMode: 'light' | 'dark'): void {
    const htmlElement = this.document.documentElement;
    if (!htmlElement) {
      return;
    }

    htmlElement.classList.remove('app-theme-light', 'app-theme-dark');

    htmlElement.classList.add(`app-theme-${themeMode}`);
    htmlElement.setAttribute('data-theme', themeMode);
  }
}
